import { Fragment, useState } from "react"
import { Link } from "react-router-dom"
import {
  ArrowUpRight,
  ChevronDown,
  Clock,
  CheckCircle,
  RotateCcw
} from "lucide-react"
import { DateTime } from "luxon"
import { cn } from "../../utils/classnames"
import { LeadScore } from "../../../types"
import { CategoryChip, ScoreBadge } from "./score-row-shared"
import { cleanSuggestedAction } from "./category-helpers"
import { leadScoreTableRowGridClass } from "./lead-score-table-grid"
import aiIcon from "../../../assets/icons/ai.svg"
import { formatPhoneNumber } from "../../utils"

interface Props {
  score: LeadScore
  index?: number
  isSelected?: boolean
  isOptimisticallyAcked?: boolean
  onToggleSelect?: (id: string) => void
  onAcknowledge?: (id: string) => void
  onUndoAcknowledge?: (id: string) => void
}

const formatCurrency = (value: number) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0
  }).format(value)

const LeadScoreCard = ({
  score,
  index = 0,
  isSelected = false,
  isOptimisticallyAcked = false,
  onToggleSelect,
  onAcknowledge,
  onUndoAcknowledge
}: Props) => {
  const [expanded, setExpanded] = useState(false)
  const sub = score.subscription
  const lo = score.loan_officer
  const quote = sub?.quote_info

  const hasCheckboxColumn = onToggleSelect != null
  const rowGridClass = leadScoreTableRowGridClass(hasCheckboxColumn)

  const isAcknowledged = isOptimisticallyAcked || score.acknowledged_at != null

  const displayName =
    sub?.first_name && sub?.last_name
      ? `${sub.first_name} ${sub.last_name}`
      : sub?.email ?? ""

  const scoredAtTimestamp = Number(score.scored_at)
  const scoredAtRelative =
    scoredAtTimestamp && !isNaN(scoredAtTimestamp)
      ? DateTime.fromSeconds(scoredAtTimestamp).toRelative() ?? ""
      : ""

  const location = quote?.location
  const locationStr = [location?.city, location?.state]
    .filter(Boolean)
    .join(", ")

  const loanMeta = [
    quote?.loan_amount != null
      ? `${formatCurrency(quote.loan_amount)} Loan`
      : null,
    quote?.loan_type,
    quote?.loan_term ? `${quote.loan_term}Yr Fixed` : null
  ].filter(Boolean) as string[]

  return (
    <div
      className={cn(
        "transition-all duration-300",
        "animate-fade-slide-in",
        isAcknowledged ? "bg-neutral-50/70" : "bg-white",
        isSelected && "bg-primary-50/30 ring-1 ring-inset ring-primary-200"
      )}
      style={{ animationDelay: `${index * 0.04}s`, animationFillMode: "both" }}
    >
      <div className="py-4 md:py-5">
        <div className={cn(rowGridClass, "min-w-0")}>
          {onToggleSelect ? (
            <input
              type="checkbox"
              checked={isSelected}
              onChange={() => onToggleSelect(score.id)}
              className="mt-2 h-4 w-4 shrink-0 cursor-pointer self-start rounded border-neutral-300 text-primary-400 focus:ring-primary-400"
              aria-label={`Select ${displayName}`}
            />
          ) : null}
          <div className="flex min-w-0 w-full max-w-full items-start gap-3 max-md:col-span-full md:contents">
            <div
              className={cn(
                "shrink-0",
                isAcknowledged &&
                  "opacity-90 grayscale-[12%] transition-all duration-300"
              )}
            >
              <ScoreBadge variant="row" score={Number(score.intent_score)} />
            </div>
            <div className="min-w-0 flex-1 md:min-w-0">
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                <span
                  className={cn(
                    "text-base font-bold leading-6 text-[#232227]",
                    isAcknowledged && "text-neutral-500"
                  )}
                >
                  {displayName}
                </span>
                <CategoryChip category={score.category} />
                {!isAcknowledged ? (
                  <span className="inline-flex shrink-0 items-center justify-center rounded bg-[#786ff6] px-1 pt-px text-[10px] font-bold leading-[14px] text-white">
                    NEW
                  </span>
                ) : null}
              </div>
              {loanMeta.length > 0 ? (
                <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-sm text-stone-500">
                  {loanMeta.map((part, i) => (
                    <Fragment key={part}>
                      {i > 0 ? (
                        <span
                          className="inline-block h-1 w-1 shrink-0 rounded-full bg-stone-500"
                          aria-hidden
                        />
                      ) : null}
                      <span>{part}</span>
                    </Fragment>
                  ))}
                </div>
              ) : null}
              {locationStr ? (
                <p className="mt-0.5 text-sm leading-[22px] text-stone-500">
                  {locationStr}
                </p>
              ) : null}
            </div>
            <Link
              to={`/engagements/${score.subscription_id}`}
              className="ml-auto shrink-0 self-start rounded-md p-1.5 text-[#4a1b9d] transition-colors hover:bg-violet-50 hover:text-[#3a157d] md:hidden"
              aria-label={
                displayName ? `View lead — ${displayName}` : "View lead"
              }
            >
              <ArrowUpRight size={22} className="shrink-0" aria-hidden />
            </Link>
          </div>

          <div
            className={cn(
              "max-md:col-span-full max-md:flex max-md:w-full max-md:items-start",
              "md:contents"
            )}
          >
            <div className="flex min-w-0 flex-col gap-1 text-sm leading-5 text-stone-500 max-md:min-w-0 max-md:flex-1 max-md:border-r max-md:border-neutral-200">
              {sub?.email ? (
                <span className="break-words md:whitespace-nowrap md:overflow-hidden md:text-ellipsis">
                  {sub.email}
                </span>
              ) : null}
              {sub?.phone ? (
                <a
                  href={`tel:${sub.phone}`}
                  className="text-left text-stone-500 hover:text-[#232227]"
                  onClick={e => e.stopPropagation()}
                >
                  {formatPhoneNumber(sub.phone)}
                </a>
              ) : null}
            </div>

            <div className="flex min-w-0 flex-col gap-1 text-left text-sm leading-5 text-stone-500 max-md:min-w-0 max-md:flex-1 max-md:pl-4">
              {lo?.name ? <span>{lo.name}</span> : null}
              {scoredAtRelative ? <span>{scoredAtRelative}</span> : null}
            </div>
          </div>

          <div className="flex min-w-0 flex-col items-start gap-2 text-left ">
            <div className="flex flex-wrap items-center gap-2">
              {!isAcknowledged && onAcknowledge ? (
                <button
                  type="button"
                  onClick={() => onAcknowledge(score.id)}
                  className="group flex items-center gap-1 rounded-full px-2 py-1 text-xs text-primary-400 transition-all hover:bg-primary-50"
                  aria-label="Mark as reviewed"
                >
                  <CheckCircle size={14} aria-hidden />
                  <span className="hidden sm:inline">Reviewed</span>
                </button>
              ) : isAcknowledged && onUndoAcknowledge ? (
                <button
                  type="button"
                  onClick={() => onUndoAcknowledge(score.id)}
                  className="group flex items-center gap-1 rounded-full px-2 py-1 text-xs text-neutral-500 transition-all hover:bg-neutral-100"
                  aria-label="Mark as new"
                >
                  <RotateCcw size={12} aria-hidden />
                  <span className="hidden sm:inline">New</span>
                </button>
              ) : null}
              <Link
                to={`/engagements/${score.subscription_id}`}
                className="hidden items-center gap-1 text-base font-medium text-[#4a1b9d] transition-colors hover:text-[#3a157d] md:inline-flex"
              >
                View Lead
                <ArrowUpRight size={18} className="shrink-0" aria-hidden />
              </Link>
            </div>
          </div>
        </div>

        <div className="mt-4">
          <p
            className={cn(
              "text-xs leading-5 text-[#151c27]",
              isAcknowledged && "text-neutral-500"
            )}
          >
            {score.summary}
          </p>

          <div className="mt-3 flex items-start gap-2 rounded-md border border-[#4a03ff] bg-[#faf9ff] px-3 py-3">
            <img src={aiIcon} alt="" className="mt-0.5 h-5 w-5 shrink-0" />
            <p className="text-sm font-semibold leading-5 text-[#151c27]">
              {cleanSuggestedAction(score.suggested_action)}
            </p>
          </div>

          {score.key_signals?.length ? (
            <div className="mt-3 flex flex-wrap gap-2">
              {score.key_signals.map((signal, i) => (
                <span
                  key={`${signal}-${i}`}
                  className="rounded-md border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-medium text-slate-500"
                >
                  {signal}
                </span>
              ))}
            </div>
          ) : null}

          <button
            type="button"
            onClick={() => setExpanded(!expanded)}
            className="mt-4 flex items-center gap-2 text-sm text-stone-500 transition-colors hover:text-neutral-600"
          >
            <ChevronDown
              size={20}
              className={cn(
                "shrink-0 transition-transform duration-200",
                expanded && "rotate-180"
              )}
              aria-hidden
            />
            {expanded ? "Hide AI reasoning" : "View AI reasoning"}
          </button>

          {expanded ? (
            <div className="mt-3 border-t border-slate-100 pt-3">
              <p className="flex items-start gap-1.5 text-xs leading-relaxed text-neutral-500">
                <Clock size={12} className="mt-0.5 shrink-0" aria-hidden />
                {score.reasoning}
              </p>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  )
}

export default LeadScoreCard
